import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink, Images } from "lucide-react";
import { api } from "@/lib/api";
import { responsive } from "@/lib/media";
import { Lightbox } from "@/components/Lightbox";

const SAMPLES = [
  { id: "s1", name: "Marina S.", where: "Brentwood", quote: "Got on a video call, David had me point the phone at the receiver, and ten minutes later the theater worked again.", stars: 5 },
  { id: "s2", name: "Alex R.", where: "Small business owner", quote: "He walked our staff through the camera app over Zoom and fixed our Wi-Fi mess on the same call. Honest pricing.", stars: 5 },
  { id: "s3", name: "Priya K.", where: "Studio City", quote: "Ask Dr.Gadget is a lifesaver — I send a photo, he sends a fix. This is how tech support should be.", stars: 5 },
];

const CLAMP = 260;

function ReviewQuote({ id, text }) {
  const [open, setOpen] = useState(false);
  const long = text.length > CLAMP + 40;
  const shown = long && !open ? text.slice(0, CLAMP).trimEnd() + "…" : text;
  return (
    <>
      <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap break-words">"{shown}"</p>
      {long && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="mt-2 text-xs text-cyan-300 hover:text-cyan-200 min-h-[32px]" data-testid={`review-more-${id}`}>
          {open ? "Show less" : "Read the full review"}
        </button>
      )}
    </>
  );
}

function ReviewPhoto({ review, onOpen }) {
  const photos = review.photos || [];
  if (!photos.length) return null;
  return (
    <button type="button" onClick={onOpen} className="relative block w-full aspect-[4/3] rounded-lg overflow-hidden border border-slate-700 bg-slate-900 mb-4 group" aria-label="See the customer's photo" data-testid={`review-photo-${review.id}`}>
      <img {...responsive(photos[0], "(max-width: 768px) 100vw, 33vw")} alt={`${review.name}'s finished job`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
      <span className="absolute bottom-2 left-2 dg-chip !bg-slate-950/80 !text-slate-200 inline-flex items-center gap-1"><Images className="w-3 h-3" />{photos.length > 1 ? `${photos.length} photos` : "Customer photo"}</span>
    </button>
  );
}

export function Testimonials() {
  const [reviews, setReviews] = useState(null);
  const [google, setGoogle] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
    api.get("/reviews/links").then((r) => setGoogle(r.data.google_review_url || "")).catch(() => {});
  }, []);
  const items = reviews?.length ? reviews : SAMPLES;
  const real = !!reviews?.length;
  const lightbox = useMemo(() => items.flatMap((r) => (r.photos || []).map((src) => ({ src, title: `${r.name}${r.where ? ` · ${r.where}` : ""}`, subtitle: "Customer photo", reviewId: r.id }))), [items]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="review-wall">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="dg-eyebrow">{real ? "Real customer reviews" : "Word of mouth"}</span>
          <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold font-display tracking-tight">Clients keep calling us back.</h2>
        </div>
        {real && <span className="dg-chip" data-testid="review-count">{reviews.length} review{reviews.length === 1 ? "" : "s"}</span>}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {items.map((t, i) => (
          <div key={t.id} className={`dg-card rounded-xl p-6 dg-reveal dg-reveal-${(i % 4) + 1}`} data-testid={`review-card-${t.id}`}>
            <ReviewPhoto review={t} onOpen={() => setOpen(lightbox.findIndex((p) => p.reviewId === t.id))} />
            <div className="text-amber-400 text-sm mb-2 font-mono">{"★".repeat(t.stars)}</div>
            <ReviewQuote id={t.id} text={t.quote} />
            <div className="mt-4 text-xs text-slate-500 font-mono uppercase tracking-widest">{t.name}{t.where ? ` · ${t.where}` : ""}{t.source ? ` · via ${t.source}` : ""}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 rounded-2xl border border-[#FFD600]/30 bg-[#FFD600]/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="review-cta">
        <div>
          <div className="font-display font-semibold text-white text-lg">Worked with David? Tell people how it went.</div>
          <p className="text-slate-400 text-sm mt-1">Takes a minute — add a photo of the finished job if you like. David reads every review before it goes up here.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <Link to="/review" className="dg-btn-primary rounded-full px-5 py-3 min-h-[44px] text-sm inline-flex items-center justify-center gap-2" data-testid="review-cta-btn">
            <Star className="w-4 h-4" /> Leave a review
          </Link>
          {google && (
            <a href={google} target="_blank" rel="noreferrer" className="dg-btn-ghost rounded-full px-5 py-3 min-h-[44px] text-sm inline-flex items-center justify-center gap-2" data-testid="review-cta-google">
              <ExternalLink className="w-4 h-4" /> Review us on Google
            </a>
          )}
        </div>
      </div>
      <Lightbox items={lightbox} index={open} onClose={() => setOpen(null)} onChange={setOpen} />
    </section>
  );
}
